import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { Route, Switch, Redirect } from 'react-router-dom';
import { makeStyles, useTheme } from '@material-ui/core/styles';
import Snackbar from '@material-ui/core/Snackbar';
import MuiAlert from '@material-ui/lab/Alert';
import { CssBaseline, AppBar, Backdrop, Drawer, useMediaQuery } from '@material-ui/core';
import { useSelector, useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import io from 'socket.io-client';
import * as actions from '../../store/index';
import DrawerExamList from './DrawerExamList/DrawerExamList';
import { DRAWER_TRANSITION_MS, EXAM_TABLE_WIDTH } from './DrawerExamList/Tables/examTableColumnWidths';
import DrawerAvatar from './DrawerAvatar/DrawerAvatar';
import NavBar from './NavigationBar/navBar';
import Dashboard from './Dashboard/Dashboard';
import ResultView from './ResultView/ResultView';
import Settings from './Dialogs/Settings/Settings';
import Info from './Dialogs/Info/Info';
import SendDicoms from './Dialogs/SendDicoms/SendDicoms';
import Report from './Dialogs/Report/Report';
import ConfirmDialog from './Dialogs/Confirm/ConfirmDialog';
import TextInfo from './Dialogs/TextInfo/TextInfo';
import LabelingStats from './Dialogs/LabelingStats/LabelingStats';
import DataExport from './Dialogs/DataExport/DataExport';
import { AI_WEBSOCKET, DATA_WEBSOCKET } from '../../config';
import ServerLogs from './Dialogs/ServerLogs/ServerLogs';
import ContextMenu from './ContextMenu/ContextMenu';
import Sidebar from './Sidebar/Sidebar';
import DrawerModelList from './DrawerModelList/DrawerModelList';
import TrainingDialog from './Dialogs/TrainingDialog/TrainingDialog';
import FilterDrawer from './FilterDrawer/FilterDrawer';
import Premium from './Dialogs/Premium/Premium';
import VersionChangeLogs from './Dialogs/ChangeLogs/VersionChangeLogs';
import RiskAssessmentForm from './Dialogs/RiskManagement/RiskAssessment/RiskAssessmentForm';
import RiskAssessmentReport from './Dialogs/RiskManagement/RiskAssessment/RiskAssessmentReport';
import UserManagement from './Dialogs/UserManagement/UserManagement';
import IfuLanguage from './Dialogs/IFU/IfuLanguage';
import LimitationsDialog from './ResultView/Summary/Limitations/LimitationsDialog';
import Configuration from './Dialogs/Configuration/Configuration';
import clsx from 'clsx';

const transitionDuration = DRAWER_TRANSITION_MS;

const useStyles = makeStyles((theme) => ({
  root: {
    display: 'flex',
  },

  appBar: {
    height: '4vh',
    transition: theme.transitions.create(['margin', 'width'], {
      easing: theme.transitions.easing.sharp,
      duration: transitionDuration,
    }),
  },

  drawer: {
    flexShrink: 0,
    whiteSpace: 'nowrap',
  },
  drawerPaper: {
    transition: `width ${transitionDuration}ms ease-in-out`,
    overflowX: 'hidden',
    marginLeft: theme.drawerWidthMenu,
    border: 'none',
    background: 'linear-gradient(180deg, rgba(66,66,66,1) 0%, rgb(75 75 75) 50%, rgba(66,66,66,1) 100%)',
    boxShadow: '0px 0px 5px 1px rgb(51 51 51)',
  },
  content: {
    flexGrow: 1,
    marginTop: '4vh',
    transition: theme.transitions.create('margin', {
      easing: theme.transitions.easing.sharp,
      duration: transitionDuration,
    }),
  },
  contentShift: {
    marginLeft: 0,
    transition: theme.transitions.create('margin', {
      easing: theme.transitions.easing.easeOut,
      duration: transitionDuration,
    }),
  },
  backdrop: {
    zIndex: theme.zIndex.drawer - 1,
    backgroundColor: 'rgba(0,0,0,0.5)',
    transition: `opacity ${transitionDuration}ms ease-in-out`,
  },
}));

function Layout() {
  const classes = useStyles();
  const dispatch = useDispatch();
  const theme = useTheme();
  const { t } = useTranslation();
  // Store state
  const filter = useSelector((state) => state.filter);
  const dates = useSelector((state) => state.dates);
  const {
    currentExam,
    currentlyProcessed,
    totalExaminations,
    pagination,
    order,
    orderBy,
    fetchingWorklist,
  } = useSelector((state) => state.examinations);

  const {
    info,
    snackbar,
    contextMenu,
    notifications,
    activeDialog,
    serverLogs,
    actionToConfirm,
    examinationListDrawer,
    tabsIndex,
  } = useSelector((state) => state.gui);
  const results = useSelector((state) => state.results);

  const smallScreen = useMediaQuery('(max-width: 1200px)') || examinationListDrawer;
  const drawerWidth = theme.drawerWidth;

  // Local State
  const [openAvatar, setOpenAvatar] = useState(false);
  const [filterIsActive, setFilterIsActive] = useState(false);
  const [bboxSocket, setBboxSocket] = useState(null);
  const [dataIsReady, setDataIsReady] = useState(null);
  const [showChangelog, setShowChangeLog] = useState(false);
  const [expandedPatientList, setExpandedPatientList] = useState(false);
  const [width, setWidth] = useState(drawerWidth);
  const shrinkLayoutPendingRef = useRef(false);
  const fetchingWorklistRef = useRef(false);
  fetchingWorklistRef.current = fetchingWorklist;

  const releaseDateVersion = process.env.REACT_APP_RELEASE_DATE;
  const last_login = localStorage.getItem('last_login');

  // WebSocket signals
  const onMessageSignal = (msg, kwargs) => {
    t(`api_messages.${msg}`, kwargs);
  };

  const onUpdateSignal = useCallback(
    (payload) => {
      let exam_id = null;
      let finished = null;
      if (payload) {
        exam_id = payload.exam_id;
        finished = payload.finished;
      }
      if (exam_id === currentExam) {
        console.log('update!!! ;)');
        dispatch(actions.fetchResults());
      } else {
        console.log('no update.');
      }

      fetchInstitutionsDevicesOperators();
      if (finished !== false) {
        dispatch(actions.setCurrentlyProcessed(null));
        dispatch(actions.setPendingReprocessing(false));
      }
      dispatch(actions.removePrior(exam_id));
    },
    [currentExam, dispatch],
  );

  const onProcessingSignal = (exam_id) => {
    dispatch(actions.setCurrentlyProcessed(exam_id));
    dispatch(actions.setPendingReprocessing(true));
  };

  const onDataExportSignal = (event) => {
    const data = JSON.parse(event.data);
    if (data.event === 'completed') {
      dispatch(
        actions.setSnackbar({
          msg: 'api_messages.data_ready_to_download',
          severity: 'success',
        }),
      );
      setDataIsReady(true);
    }
  };

  const fetchInstitutionsDevicesOperators = () => {
    dispatch(actions.fetchTotalExaminations());
    dispatch(actions.fetchInstitutions());
    dispatch(actions.fetchDevices());
    dispatch(actions.fetchOperators());
  };

  const ClearInstitutionsDevicesOperators = () => {
    dispatch(actions.setTotalExaminations(0));
    dispatch(actions.setDeviceList([]));
    dispatch(actions.setOperatorList([]));
    dispatch(actions.setInstitutionList([]));
  };

  // Handle Patient List Drawer
  const handleDrawerPaperTransitionEnd = (event) => {
    if (event.propertyName !== 'width') return;
    if (!shrinkLayoutPendingRef.current) return;
    shrinkLayoutPendingRef.current = false;
    setExpandedPatientList(false);
  };

  const handleExpandToTemporary = () => {
    shrinkLayoutPendingRef.current = false;
    setExpandedPatientList(true);
    setWidth(EXAM_TABLE_WIDTH.expanded);
  };

  const handleShrinkToPersistent = () => {
    shrinkLayoutPendingRef.current = true;
    setWidth(drawerWidth);
  };

  const handleCloseDrawer = (scrollTop) => {
    shrinkLayoutPendingRef.current = false;
    dispatch(actions.setScrollPosition(scrollTop));
    dispatch(actions.setExaminationListDrawer(false));
    setExpandedPatientList(false);
    setWidth(drawerWidth);
  };

  // Lifecycle
  useEffect(() => {
    if (
      dates.selectedRange === 'day' &&
      window.location.pathname.includes('details') &&
      tabsIndex !== 2 &&
      currentlyProcessed &&
      currentExam !== currentlyProcessed
    ) {
      dispatch(actions.setCurrentExam(currentlyProcessed));
    }
  }, [filter, currentlyProcessed]);

  useEffect(() => {
    dispatch(actions.fetchGlobalSettings());

    dispatch(actions.setSnackbar(null));

    // WebSocket AI
    const socket = io(AI_WEBSOCKET, {
      withCredentials: true,
      transports: ['websocket'],
    });

    socket.on('connect', () => {
      console.log('Connected to AI server');
    });

    socket.on('disconnect', (reason) => {
      console.log('Connection to AI server lost:', reason);
    });

    socket.on('connect_error', (error) => {
      console.error('AI server connection error:', error);
    });

    socket.on('message', ([msg, kwargs]) => {
      onMessageSignal(msg, kwargs);
    });

    socket.on('snackbar', (data) => {
      const translation = t(`api_messages.${data.msg}`, data.kwargs);
      dispatch(
        actions.setSnackbar({
          msg: translation,
          severity: data.severity,
          duration: data.duration,
        }),
      );
    });

    socket.on('processing', (exam_id) => {
      onProcessingSignal(exam_id);
    });

    setBboxSocket(socket);

    // WebSocket Data Export
    const dataSocket = new WebSocket(DATA_WEBSOCKET);
    dataSocket.onopen = () => {
      console.log('Connected to Data Export server');
    };
    dataSocket.onclose = (event) => {
      console.log('Connection to Data Export server lost:', event);
    };
    dataSocket.onerror = (error) => {
      console.error('Data Export server connection error:', error);
    };
    dataSocket.onmessage = onDataExportSignal;

    dispatch(actions.fetchNotifications(localStorage.getItem('username')));

    return () => {
      if (!!socket) {
        socket.off('message');
        socket.off('update');
        socket.off('worklist');
        socket.off('snackbar');
        socket.off('processing');
        socket.close();
      }
      if (!!dataSocket) {
        dataSocket.close();
      }
    };
  }, [dispatch]);

  useEffect(() => {
    if (!bboxSocket) return;

    const handleUpdate = (payload) => onUpdateSignal(payload);
    bboxSocket.on('update', handleUpdate);

    return () => {
      bboxSocket.off('update', handleUpdate);
    };
  }, [bboxSocket, onUpdateSignal]);

  useEffect(() => {
    if (!bboxSocket) return;

    const handleWorklist = () => {
      // Manual MWL sync owns the DB reload; ignore socket until it finishes.
      if (fetchingWorklistRef.current) return;
      dispatch(actions.fetchWorklist(t, false));
    };
    bboxSocket.on('worklist', handleWorklist);
    handleWorklist();
    return () => {
      bboxSocket.off('worklist', handleWorklist);
    };
  }, [bboxSocket, dispatch, t]);

  useEffect(() => {
    const hasNonSearchDateFilters = Object.entries(filter || {}).some(([key, value]) => {
      if (key === 'search' || key === 'dates') return false;
      if (value === undefined || value === null) return false;
      if (typeof value === 'string') return value.trim() !== '';
      if (Array.isArray(value)) return value.length > 0;
      return true;
    });
    const isActive = hasNonSearchDateFilters;
    setFilterIsActive(isActive);
    fetchInstitutionsDevicesOperators();
    return () => {
      ClearInstitutionsDevicesOperators();
    };
  }, [filter]);

  useEffect(() => {
    if (!currentExam && (pagination[0] !== 0 || pagination[1] !== 5)) {
      dispatch(actions.setPagination([0, 40]));
    }
  }, [filter.dates, examinationListDrawer]);

  useEffect(() => {
    dispatch(actions.fetchExaminations());
  }, [pagination, order, orderBy, totalExaminations]);

  useEffect(() => {
    const lastLoginDate = last_login ? new Date(last_login) : null;
    const lastReleaseDate = releaseDateVersion ? new Date(releaseDateVersion) : null;
    const releaseNotesShown = localStorage.getItem(`releaseNotesShown_${releaseDateVersion}`);
    if (releaseDateVersion && lastLoginDate && !releaseNotesShown) {
      if (lastReleaseDate > lastLoginDate) {
        setShowChangeLog(true);
        localStorage.setItem(`releaseNotesShown_${releaseDateVersion}`, true);
      } else setShowChangeLog(false);
    }
  }, [last_login, releaseDateVersion]);

  const translateOrFallback = (snackbarInfo) => {
    const { msg, msgParams } = snackbarInfo;
    const translated = t(msg, msgParams);
    if (translated === msg) {
      if (msg.includes('api_messages.') && translated === msg) {
        return msg.replace('api_messages.', '');
      } else return msg;
    }
    return translated;
  };

  // Handle Main Container Styles
  const mainClassName = useMemo(
    () =>
      clsx(classes.content, {
        [classes.contentShift]: examinationListDrawer && !expandedPatientList,
      }),
    [classes.content, classes.contentShift, examinationListDrawer, expandedPatientList, smallScreen],
  );

  const mainStyle = useMemo(
    () => ({
      marginLeft: examinationListDrawer && !expandedPatientList ? `${width}px` : 0,
      height: currentExam ? '96vh' : '93.5vh',
    }),
    [examinationListDrawer, expandedPatientList, results, currentExam, smallScreen, width],
  );

  let mainLayoutComponents = null;
  if (localStorage.getItem('role') === 'service') {
    dispatch(actions.setActiveDialog('settings'));
    mainLayoutComponents = <Settings />;
  } else {
    mainLayoutComponents = (
      <main className={mainClassName} style={mainStyle}>
        <CssBaseline />

        {/* Patient / ExaminationList Drawer */}
        <Drawer
          className={classes.drawer}
          variant='persistent'
          anchor='left'
          open={examinationListDrawer}
          classes={{ paper: classes.drawerPaper }}
          PaperProps={{
            style: { width },
            onTransitionEnd: handleDrawerPaperTransitionEnd,
          }}>
          {/* Patient List / Examination List Drawer */}
          {examinationListDrawer && window.location.pathname.includes('results') && (
            <DrawerExamList
              expandedPatientList={expandedPatientList}
              drawerWidth={width}
              filterIsActive={filterIsActive}
              handleExpandToTemporary={handleExpandToTemporary}
              handleShrinkToPersistent={handleShrinkToPersistent}
              handleCloseDrawer={handleCloseDrawer}
            />
          )}
        </Drawer>

        {/* TODO: Revise DrawerModelList not used or fix  */}
        {examinationListDrawer && window.location.pathname.includes('training') && <DrawerModelList />}

        {/* Backdrop for the examination list drawer */}
        {expandedPatientList && examinationListDrawer && (
          <Backdrop
            className={classes.backdrop}
            open={true}
            transitionDuration={transitionDuration}
            onClick={handleCloseDrawer}
          />
        )}

        <Switch>
          <Route path='/bbox/results/dashboard'>
            <Dashboard />
          </Route>

          <Route path='/bbox/results/details/:study_id?'>
            <ResultView />
          </Route>

          <Redirect to='/bbox/results/dashboard' />
        </Switch>
      </main>
    );
  }

  return (
    <div className={classes.root}>
      <CssBaseline />

      {/* Top Navigation Bar */}
      <AppBar position='fixed' className={classes.appBar}>
        <NavBar
          activeFilter={filterIsActive}
          leftAlignment={
            examinationListDrawer ? (expandedPatientList ? '72px' : `calc(${width}px + 72px)`) : '72px'
          }
        />
      </AppBar>

      {/* Sidebar Main Menu */}
      <Sidebar
        dataIsReady={dataIsReady}
        notificationsLength={notifications?.length}
        onAvatarClick={() => {
          setOpenAvatar((prevState) => !prevState);
          dispatch(actions.setExaminationListDrawer(false));
        }}
        onExamListOpen={() => {
          dispatch(actions.setExaminationListDrawer(!examinationListDrawer));
          setOpenAvatar(false);
        }}
      />

      {mainLayoutComponents}

      {snackbar && (
        <Snackbar
          open={!!snackbar}
          autoHideDuration={snackbar.duration || 3500}
          onClose={() => dispatch(actions.setSnackbar(null))}
          style={{ zIndex: 2500 }}>
          <MuiAlert elevation={6} variant='filled' severity={snackbar.severity} style={{ zIndex: 2500 }}>
            {translateOrFallback(snackbar)}
          </MuiAlert>
        </Snackbar>
      )}

      {openAvatar && (
        <DrawerAvatar
          onClose={() => setOpenAvatar(false)}
          open={openAvatar}
          fetchOperators={() => dispatch(actions.fetchOperators())}
          onAvatarClick={() => {
            setOpenAvatar((prevState) => !prevState);
            dispatch(actions.setExaminationListDrawer(false));
          }}
        />
      )}
      {activeDialog === 'filter' && <FilterDrawer />}
      {activeDialog === 'settings' && <Settings />}
      {activeDialog === 'config' && <Configuration />}
      {activeDialog === 'info' && <Info setShowChangeLog={setShowChangeLog} />}
      {activeDialog === 'upload' && <SendDicoms setSnackbar={(obj) => dispatch(actions.setSnackbar(obj))} />}
      {activeDialog === 'report' && <Report open={activeDialog === 'report'} />}
      {activeDialog === 'risk_management_form' && <RiskAssessmentForm results={results} />}
      {activeDialog === 'risk_management' && <RiskAssessmentReport results={results} />}
      {activeDialog === 'LabelStat' && (
        <LabelingStats
          open={activeDialog === 'LabelStat'}
          onClose={() => dispatch(actions.setActiveDialog(null))}
        />
      )}
      {activeDialog === 'export' && (
        <DataExport
          open={activeDialog === 'export'}
          onClose={() => dispatch(actions.setActiveDialog(null))}
          setSnackbar={(obj) => dispatch(actions.setSnackbar(obj))}
          dataIsReady={dataIsReady}
          setDataIsReady={setDataIsReady}
        />
      )}
      {activeDialog === 'server' && (
        <ServerLogs
          open={activeDialog === 'server'}
          onClose={() => dispatch(actions.setActiveDialog(null))}
          text={serverLogs}
        />
      )}
      {!!actionToConfirm && (
        <ConfirmDialog
          action={actionToConfirm.action}
          text={actionToConfirm.text}
          open={!!actionToConfirm}
          onClose={() => dispatch(actions.setActionToConfirm(null))}
          dialogTitle={actionToConfirm?.dialogTitle}
        />
      )}
      {!!info && <TextInfo text={info} open={!!info} onClose={() => dispatch(actions.setInfo(null))} />}
      {activeDialog === 'users' && <UserManagement open={activeDialog === 'users'} />}
      {activeDialog === 'ifu_language' && <IfuLanguage open={activeDialog === 'ifu_language'} />}
      {!!contextMenu && (
        <ContextMenu
          isOpen={!!contextMenu}
          onClose={() => dispatch(actions.setContextMenu(null))}
          items={contextMenu.items}
          left={contextMenu.left}
          top={contextMenu.top}
        />
      )}
      {activeDialog === 'training' && (
        <TrainingDialog
          open={activeDialog === 'training'}
          onClose={() => dispatch(actions.setActiveDialog(null))}
        />
      )}
      {activeDialog === 'premium_upload' && (
        <Premium
          open={activeDialog === 'premium_upload'}
          onClose={() => dispatch(actions.setActiveDialog(null))}
          section={'upload'}
        />
      )}
      {activeDialog === 'premium_export' && (
        <Premium
          open={activeDialog === 'premium_export'}
          onClose={() => dispatch(actions.setActiveDialog(null))}
          section={'export'}
        />
      )}
      {activeDialog === 'limitations' && (
        <LimitationsDialog
          open={activeDialog === 'limitations'}
          onClose={() => dispatch(actions.setActiveDialog(null))}
          section={'limitations'}
        />
      )}

      <VersionChangeLogs
        open={showChangelog}
        onClose={() => setShowChangeLog(false)}
        title={t('Layout.release_notes')}
      />
    </div>
  );
}

export default Layout;
